import { useQuery } from '@apollo/client/react';
import React from 'react';
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Edit,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useRecordContext,
} from 'react-admin';
import {
  AdminExerciseDetailsFragment,
  ExerciseUnitsDocument,
  Prefix,
} from '../../../graphql/types';
import FrameCapturePreview from '../components/FrameCapturePreview';
import ImageUploadInput from '../components/ImageUploadInput';
import VideoUploadInput from '../components/VideoUploadInput';
import { ArchiveResourceButton } from './CategoryActions';

const ExerciseEditToolbar: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();
  const isArchived = !!record?.isArchived;

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton disabled={isArchived} />
      <ArchiveResourceButton resource="Exercise" />
    </Toolbar>
  );
};

const ExerciseEditView: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();
  const isArchived = !!record?.isArchived;
  const [capturedFrame, setCapturedFrame] = React.useState<string | null>(null);

  const { data: exerciseUnitsData, loading: defaultUnitsLoading } = useQuery(
    ExerciseUnitsDocument,
  );
  const defaultUnits = (exerciseUnitsData?.exerciseUnits || []).map((unit) => ({
    id: unit.value,
    name: unit.name,
  }));

  const handleFrameCapture = React.useCallback((dataUrl: string) => {
    setCapturedFrame(dataUrl);
  }, []);

  const handleReplaceThumbnail = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  const handleClearFrame = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  return (
    <SimpleForm toolbar={<ExerciseEditToolbar />}>
      <TextInput source="id" slotProps={{ input: { disabled: true } }} />
      <TextInput source="apmlId" disabled />
      <ReferenceInput
        source="exerciseBase.id"
        reference="ExerciseBase"
        disabled={isArchived}
      >
        <AutocompleteInput
          optionText="name"
          filterToQuery={(name: string) => ({ name })}
          label="Base Movement"
          fullWidth
        />
      </ReferenceInput>
      <TextInput source="name" fullWidth required disabled={isArchived} />
      <TextInput
        source="displayName"
        fullWidth
        disabled={isArchived}
        helperText='This will override "Name" in the app. If empty, "Name" will be used.'
        sx={{ marginBottom: 4 }}
      />
      <ArrayInput source="coachingKeys" label="Instructions">
        <SimpleFormIterator disabled={isArchived}>
          <TextInput
            source=""
            fullWidth
            label="Instruction"
            style={{ width: '50vw' }}
          />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput
        source="modifications"
        fullWidth
        multiline
        disabled={isArchived}
        label="Modifications"
      />
      <TextInput
        source="coachNotes"
        fullWidth
        multiline
        disabled={isArchived}
        label="Coach Notes"
      />
      <SelectInput
        isPending={defaultUnitsLoading}
        source="defaultUnit.value"
        label="Default Unit"
        choices={defaultUnits}
        disabled={isArchived}
      />
      <BooleanInput source="canTrackWeight" disabled={isArchived} />
      <NumberInput
        source="progression"
        label="Progression"
        step={1}
        disabled={isArchived}
        helperText="Optional exercise-level progression (integer)."
      />
      <ArrayInput source="bodyParts" label="Body parts">
        <SimpleFormIterator disabled={isArchived}>
          <TextInput source="" fullWidth label="Body part" />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="equipment" label="Equipment">
        <SimpleFormIterator disabled={isArchived}>
          <TextInput source="" fullWidth label="Equipment" />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="oneRepMax" label="One rep max (kg)">
        <SimpleFormIterator disabled={isArchived}>
          <NumberInput source="" label="Weight (kg)" />
        </SimpleFormIterator>
      </ArrayInput>
      <VideoUploadInput
        placeholder="Drop a video file to upload, or click to select it."
        multiple={false}
        prefix={Prefix.ExerciseVideo}
        source="video"
        onFrameCapture={handleFrameCapture}
      />
      <FrameCapturePreview
        capturedFrame={capturedFrame}
        onReplaceThumbnail={handleReplaceThumbnail}
        onClearFrame={handleClearFrame}
        thumbnailSource="thumbnail"
      />
      <ImageUploadInput
        placeholder="Drop an image file to upload, or click to select it."
        multiple={false}
        prefix={Prefix.ExerciseThumbnail}
        source="thumbnail"
      />
    </SimpleForm>
  );
};

export const ExerciseEdit: React.FC = () => (
  <Edit mutationMode="optimistic" redirect={false}>
    <ExerciseEditView />
  </Edit>
);
